import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Chip,
} from '@mui/material';
import {
  CheckCircle as AvailableIcon,
  LocalShipping as OnDeliveryIcon,
  AccessTime as AwayIcon,
  PowerSettingsNew as OffDutyIcon,
} from '@mui/icons-material';
import deliveryService from '../services/delivery.service';

export default function DeliveryStatusWidget() {
  const [status, setStatus] = useState('available');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const data = await deliveryService.getStatus();
      setStatus(data.status || 'available');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching status:', error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (event: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
    if (!newStatus) return;

    try {
      setUpdating(true);
      await deliveryService.updateStatus(newStatus);
      setStatus(newStatus);
      setMessage('Status updated successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Error updating status:', error);
      setMessage(error.response?.data?.error || 'Failed to update status');
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setUpdating(false);
    }
  };

  const statusConfig = {
    available: {
      label: 'Available',
      icon: <AvailableIcon />,
      color: 'success' as const,
      description: 'Ready to accept deliveries',
    },
    on_delivery: {
      label: 'On Delivery',
      icon: <OnDeliveryIcon />,
      color: 'primary' as const,
      description: 'Currently delivering orders',
    },
    away: {
      label: 'Away',
      icon: <AwayIcon />,
      color: 'warning' as const,
      description: 'Temporarily unavailable',
    },
    off_duty: {
      label: 'Off Duty',
      icon: <OffDutyIcon />,
      color: 'error' as const,
      description: 'Not available for deliveries',
    },
  };

  const currentConfig = statusConfig[status as keyof typeof statusConfig];

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading status...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delivery Status
            </Typography>
            <Chip
              icon={currentConfig.icon}
              label={currentConfig.label}
              color={currentConfig.color}
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {currentConfig.description}
          </Typography>
        </Box>

        {message && (
          <Alert severity={message.includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <ToggleButtonGroup
          value={status}
          exclusive
          onChange={handleStatusChange}
          disabled={updating}
          fullWidth
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}
        >
          <ToggleButton value="available" sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <AvailableIcon sx={{ mb: 0.5, color: 'success.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Available
              </Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="on_delivery" sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <OnDeliveryIcon sx={{ mb: 0.5, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                On Delivery
              </Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="away" sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <AwayIcon sx={{ mb: 0.5, color: 'warning.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Away
              </Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="off_duty" sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <OffDutyIcon sx={{ mb: 0.5, color: 'error.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Off Duty
              </Typography>
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <strong>Tip:</strong> Keep your status updated to receive relevant delivery assignments
          </Typography>
          <Typography variant="caption" color="text.secondary">
            • <strong>Available:</strong> You'll receive new delivery requests<br />
            • <strong>On Delivery:</strong> Currently handling an order<br />
            • <strong>Away:</strong> Temporarily unavailable (break, etc.)<br />
            • <strong>Off Duty:</strong> Not working - won't receive requests
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
